import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useBackClose } from '@/hooks/useBackClose';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/dashboard/PageHeader';
import EstimatesList, {
  EstimatesListSkeleton,
} from '@/components/estimates/EstimatesList';
import EstimateBuilder from '@/components/estimates/EstimateBuilder';
import EstimatePreview from '@/components/estimates/EstimatePreview';
import PaymentScheduleEditor from '@/components/estimates/PaymentScheduleEditor';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertCircle,
  Plus,
  ArrowLeft,
  Save,
  Eye,
  Printer,
  ArrowRightLeft,
  Loader2,
  RefreshCw,
  Camera,
  MessageSquare,
} from 'lucide-react';
import { PQActionLabel } from '@/components/pq/PQMark';
import {
  fetchEstimates,
  saveEstimate,
  deleteEstimate,
  buildBlankEstimate,
  computeEstimateTotals,
  addSection,
  updateSection,
  removeSection,
  addBlock,
  updateBlock,
  removeBlock,
  moveBlock,
  addLineItem,
  updateLineItem,
  removeLineItem,
  addMaterialRow,
  updateMaterialRow,
  removeMaterialRow,
  addAllowanceRow,
  updateAllowanceRow,
  removeAllowanceRow,
  setSectionBottomLineMode,
  setSectionBottomLineTotal,
  setAllSectionsBottomLineMode,
  addPaymentRow,
  updatePaymentRow,
  removePaymentRow,
  convertEstimateToJob,
  applyProposalToEstimate,
  applyDraftToEstimate,
  isProposalInEstimate,
} from '@/lib/estimatesData';
import {
  TAB_IDS,
  ESTIMATE_STATUSES,
  normalizeStatus,
  reasonsForStatus,
  isReasonRequired,
} from '@/lib/estimateLifecycle';
import { statusLabel } from '@/lib/statusLabels';
import { supabase } from '@/lib/supabaseClient';
import { fetchSettingsByKey, SETTINGS_KEYS, DEFAULT_DEFAULTS } from '@/lib/settingsData';
import { startWalkthrough, fetchLatestWalkthroughForEstimate } from '@/lib/pqvData';
import PQChatPanel from '@/components/pq/PQChatPanel';
import ChecklistPanel from '@/components/checklist/ChecklistPanel';
import { toast } from 'sonner';

export default function EstimatesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The open estimate lives in the URL (?id=...) so the browser knows it is
  // a "place": Back returns to it instead of dumping the user on the list,
  // returning from PQ Vision can land inside the estimate, and reload/links
  // reopen it. Which estimate is open is DRIVEN by this param (see the sync
  // effect below); `current` holds the working copy.
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState('list'); // 'list' | 'builder' | 'preview'
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState([]);
  const [company, setCompany] = useState(null);
  const [defaults, setDefaults] = useState(DEFAULT_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  // Unsaved-changes guard: snapshot of the estimate as last saved/opened.
  // `current` is compared against this to know if there's unsaved work.
  const savedSnapshotRef = useRef(null);
  const [confirmExit, setConfirmExit] = useState(false);
  const [converting, setConverting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmConvert, setConfirmConvert] = useState(false);

  // Phase 27 — lifecycle UI state.
  const [activeTab, setActiveTab] = useState('draft'); // draft | sent | won | lost
  // PQ Vision — capture launch button state.
  const [startingWalkthrough, setStartingWalkthrough] = useState(false);
  const [pqChatOpen, setPqChatOpen] = useState(false);
  // Walkthrough already linked to the currently-open estimate (if any).
  // Drives the "PQ Vision GO" vs "PQ RE-View" button swap in the builder.
  const [existingWalkthroughId, setExistingWalkthroughId] = useState(null);
  // Outcome-reason capture dialog. `target` is the destination status the user
  // is moving the current estimate to. `reason` is the selected option value;
  // `custom` is the free-text override (empty string when unused).
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    target: null,
    reason: '',
    custom: '',
    saving: false,
  });

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [estData, clientsRes, companyRes, defaultsRes] = await Promise.all([
        fetchEstimates(),
        supabase
          // Include leads too, so an estimate can be started for a captured
          // lead (not just an existing client). The builder labels leads.
          .from('clients')
          .select('id,name,address,type')
          .in('type', ['client', 'lead'])
          .order('name', { ascending: true }),
        fetchSettingsByKey(SETTINGS_KEYS.COMPANY, {}),
        fetchSettingsByKey(SETTINGS_KEYS.DEFAULTS, DEFAULT_DEFAULTS),
      ]);
      if (clientsRes.error) throw clientsRes.error;
      setEstimates(estData);
      setClients(clientsRes.data || []);
      setCompany(companyRes?.value || null);
      setDefaults(defaultsRes?.value || DEFAULT_DEFAULTS);
    } catch (e) {
      setError(e?.message || t('estimates.errors.load'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // PQ Vision — resolve whether the currently-open estimate already has a
  // walkthrough so the builder header can swap between "PQ Vision GO" (new)
  // and "PQ RE-View" (open existing). Re-runs whenever the open estimate
  // changes (or the user navigates back to the list).
  useEffect(() => {
    let cancelled = false;
    if (!current?.id) {
      setExistingWalkthroughId(null);
      return undefined;
    }
    (async () => {
      try {
        const w = await fetchLatestWalkthroughForEstimate(current.id);
        if (!cancelled) setExistingWalkthroughId(w?.id || null);
      } catch (e) {
        // Non-fatal — fall back to "GO" if the lookup fails so the user
        // can still create a fresh walkthrough.
        if (!cancelled) setExistingWalkthroughId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [current?.id]);

  const totals = useMemo(
    () => (current ? computeEstimateTotals(current) : { subtotal: 0, tax: 0, total: 0, payment_schedule: [] }),
    [current]
  );

  // Phase 27 — partition estimates by canonical status for the 4 tabs.
  const estimatesByTab = useMemo(() => {
    const buckets = { draft: [], sent: [], won: [], lost: [] };
    for (const est of estimates) {
      const s = normalizeStatus(est?.status);
      if (buckets[s]) buckets[s].push(est);
    }
    return buckets;
  }, [estimates]);

  const tabCounts = useMemo(
    () => ({
      draft: estimatesByTab.draft.length,
      sent: estimatesByTab.sent.length,
      won: estimatesByTab.won.length,
      lost: estimatesByTab.lost.length,
    }),
    [estimatesByTab]
  );

  const visibleEstimates = estimatesByTab[activeTab] || [];

  // Serialize an estimate for dirty-checking. Stable enough: `current` is
  // plain JSON data from Supabase / the builder reducers.
  const snapshot = (est) => {
    try {
      return JSON.stringify(est);
    } catch {
      return null;
    }
  };

  const startNew = () => {
    const blank = buildBlankEstimate({ t, company, defaults });
    savedSnapshotRef.current = snapshot(blank);
    setCurrent(blank);
    setMode('builder');
  };

  // A row click opens the FULL-PAGE builder at every width. The wide-screen
  // pass briefly split this page into list | inline preview like Jobs and
  // Clients; an estimate's "detail" is a whole document, and squeezing it
  // into a viewport-capped side column made the page John actually works in
  // short and cramped (dogfood 2026-09-12: "change the estimates page back to
  // full length"). Master-detail stays on Jobs/Clients, where the detail is
  // genuinely a compact panel.
  const openEstimateState = (est) => {
    const opened = { ...est };
    savedSnapshotRef.current = snapshot(opened);
    setCurrent(opened);
    setMode('builder');
  };

  const openEstimate = (est) => {
    // Open immediately (no flicker), and record the place in the URL so
    // Back/refresh/deep-links know about it. The sync effect below sees
    // current.id === id and leaves it alone.
    openEstimateState(est);
    if (est?.id) setSearchParams({ id: est.id });
  };

  const exitToList = () => {
    savedSnapshotRef.current = null;
    setConfirmExit(false);
    setCurrent(null);
    setMode('list');
    if (searchParams.get('id')) setSearchParams({});
  };

  // URL → state sync. Runs when the ?id param changes (Back/Forward, return
  // from PQ Vision, deep link) or when the estimates list arrives on first
  // load. While dirty, useUnsavedGuard traps Back before the URL can change,
  // so this only ever closes clean builders.
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      if (current?.id === id) return;
      const est = estimates.find((e) => e.id === id);
      if (est) openEstimateState(est);
      // Unknown id (deleted, or another tenant's link): once the list has
      // actually loaded, drop the param instead of pointing at nothing.
      else if (!loading) setSearchParams({}, { replace: true });
    } else if (mode !== 'list' && current?.id) {
      // Param removed (Back out of the builder) — return to the list.
      savedSnapshotRef.current = null;
      setConfirmExit(false);
      setCurrent(null);
      setMode('list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, estimates, loading]);

  // True when the builder/preview is open with edits not yet persisted.
  const isDirty =
    mode !== 'list' &&
    !!current &&
    snapshot(current) !== savedSnapshotRef.current;

  // Route the in-app Back button (and browser Back) through a confirm when dirty.
  const attemptExit = () => {
    if (isDirty) setConfirmExit(true);
    else exitToList();
  };

  useUnsavedGuard(isDirty, () => setConfirmExit(true));

  // Phone Back closes the PQ chat sheet instead of leaving the page.
  useBackClose(pqChatOpen, () => setPqChatOpen(false));

  const handleSave = async () => {
    if (!current) return;
    if (!current.project_name?.trim()) {
      toast.error(t('estimates.project_name_required'));
      return;
    }
    setSaving(true);
    try {
      const saved = await saveEstimate(current);
      toast.success(t('estimates.saved_toast', { name: saved.project_name }));
      // Update list
      setEstimates((es) => {
        const idx = es.findIndex((e) => e.id === saved.id);
        if (idx >= 0) {
          const next = es.slice();
          next[idx] = saved;
          return next;
        }
        return [saved, ...es];
      });
      // Saved state becomes the new clean baseline for the dirty check.
      savedSnapshotRef.current = snapshot(saved);
      setCurrent(saved);
      // First save of a new estimate mints its id — put it in the URL now
      // (replace, not push: a URL correction, not a navigation).
      if (saved?.id && searchParams.get('id') !== saved.id) {
        setSearchParams({ id: saved.id }, { replace: true });
      }
    } catch (e) {
      toast.error(e?.message || t('estimates.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  // PQ Apply autosave: tapping Apply is an explicit "put this in the
  // document" — leaving it stranded in unsaved builder state lost a whole
  // applied takeoff when the page reloaded (live-fire 2026-09-07). Each
  // apply bumps the tick; the debounce collapses Apply-all into one save.
  const [pqAutosaveTick, setPqAutosaveTick] = useState(0);
  useEffect(() => {
    if (pqAutosaveTick === 0) return undefined;
    const timer = setTimeout(() => {
      handleSave();
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pqAutosaveTick]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteEstimate(confirmDelete.id);
      setEstimates((es) => es.filter((e) => e.id !== confirmDelete.id));
      toast.success(
        t('estimates.deleted_toast', {
          name: confirmDelete.project_name || confirmDelete.title || '',
        })
      );
      setConfirmDelete(null);
      if (current?.id === confirmDelete.id) {
        exitToList();
      }
    } catch (e) {
      toast.error(e?.message || t('estimates.errors.delete'));
    }
  };

  const handleConvert = async () => {
    if (!current?.id) {
      toast.error(t('estimates.save_first'));
      return;
    }
    setConverting(true);
    try {
      const result = await convertEstimateToJob(current, { clients });
      if (result.linkedExisting) {
        toast.success(t('estimates.linked_toast', { name: result.job.name }));
      } else {
        toast.success(t('estimates.converted_toast', { name: result.job.name }));
      }
      setConfirmConvert(false);
      // Refresh local list and navigate to the new job
      await load({ silent: true });
      navigate('/dashboard/active-jobs');
    } catch (e) {
      toast.error(e?.message || t('estimates.errors.convert'));
    } finally {
      setConverting(false);
    }
  };

  const handlePrint = () => {
    // Switch to preview to make sure it's mounted, then print on next frame.
    setMode('preview');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.print());
    });
  };

  // PQ Vision — a walkthrough is part 1 of an estimate. Inserts a
  // pqv_walkthrough row referencing the current estimate's id (contractor_id
  // is set by pqvData from the auth user), then navigates to the capture page.
  // Requires the estimate to be saved (have an id) so the FK is satisfiable.
  const handleStartWalkthrough = async () => {
    if (!current?.id) {
      toast.error(
        t('pqv.estimate_unsaved', {
          defaultValue: 'Save the estimate first to start a walkthrough.',
        })
      );
      return;
    }
    // If a walkthrough already exists for this estimate, just open it —
    // this is the "PQ RE-View" path. No new row, no toast.
    if (existingWalkthroughId) {
      navigate(`/dashboard/pq-vision/walkthrough/${existingWalkthroughId}`);
      return;
    }
    setStartingWalkthrough(true);
    try {
      const w = await startWalkthrough(current.id);
      setExistingWalkthroughId(w.id);
      toast.success(
        t('pqv.started_toast', { defaultValue: 'Walkthrough started' })
      );
      navigate(`/dashboard/pq-vision/walkthrough/${w.id}`);
    } catch (e) {
      toast.error(
        e?.message ||
          t('pqv.start_failed', {
            defaultValue: 'Could not start walkthrough.',
          })
      );
    } finally {
      setStartingWalkthrough(false);
    }
  };

  // Phase 27 — apply a lifecycle status change to the currently-open estimate.
  // - draft / sent transitions: patch immediately (no dialog).
  // - won / lost transitions: open the outcome-reason dialog first; this
  //   function is then invoked by `commitStatusDialog` once the user confirms.
  // For saved estimates we also persist the patch directly via supabase so the
  // user doesn't need to click Save just to move pipeline state. Unsaved
  // estimates just patch local state — they'll be persisted on first Save.
  const applyStatusChange = useCallback(
    async (newStatus, outcomeReason = '') => {
      if (!current) return;
      const normalized = normalizeStatus(newStatus);
      const nowIso = new Date().toISOString();
      const patch = {
        status: normalized,
        outcome_reason:
          normalized === 'won' || normalized === 'lost'
            ? (outcomeReason || '')
            : '',
        won_at: normalized === 'won' ? (current.won_at || nowIso) : null,
        lost_at: normalized === 'lost' ? (current.lost_at || nowIso) : null,
        sent_at:
          normalized === 'sent' && !current.sent_at ? nowIso : current.sent_at,
      };
      setCurrent((c) => ({ ...c, ...patch }));

      if (current.id) {
        try {
          const { error } = await supabase
            .from('estimates')
            .update({
              status: patch.status,
              outcome_reason: patch.outcome_reason || null,
              won_at: patch.won_at,
              lost_at: patch.lost_at,
              sent_at: patch.sent_at,
            })
            .eq('id', current.id);
          if (error) throw error;
          setEstimates((es) =>
            es.map((e) =>
              e.id === current.id ? { ...e, ...patch } : e
            )
          );
          toast.success(
            t('estimates.status_updated_toast', {
              defaultValue: 'Status updated',
            })
          );
        } catch (e) {
          toast.error(
            e?.message ||
              t('estimates.errors.status_update', {
                defaultValue: 'Failed to update status',
              })
          );
        }
      }
    },
    [current, t]
  );

  // Triggered when user picks a value from the builder header status dropdown.
  // Routes lost/won through the outcome-reason dialog; passes draft/sent
  // straight to applyStatusChange.
  const handleStatusSelect = (newStatus) => {
    const target = normalizeStatus(newStatus);
    if (!current) return;
    if (target === normalizeStatus(current.status)) return;
    if (target === 'won' || target === 'lost') {
      setStatusDialog({
        open: true,
        target,
        reason: '',
        custom: current.outcome_reason || '',
        saving: false,
      });
      return;
    }
    applyStatusChange(target);
  };

  const closeStatusDialog = () =>
    setStatusDialog({
      open: false,
      target: null,
      reason: '',
      custom: '',
      saving: false,
    });

  // Resolves the final outcome_reason string. Free-text always wins when the
  // user has typed something; otherwise we store the dropdown value verbatim
  // (the i18n label is computed at render-time from the stored value).
  const commitStatusDialog = async () => {
    const { target, reason, custom } = statusDialog;
    if (!target) return;
    const finalReason = (custom && custom.trim()) || reason || '';
    if (isReasonRequired(target) && !finalReason) {
      toast.error(
        t('estimates.outcome_required', {
          defaultValue: 'Please select or enter a reason.',
        })
      );
      return;
    }
    setStatusDialog((s) => ({ ...s, saving: true }));
    await applyStatusChange(target, finalReason);
    closeStatusDialog();
  };

  const renderHeaderActions = () => {
    if (mode === 'list') {
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="estimates-refresh-button"
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Button
            size="sm"
            onClick={startNew}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="estimates-new-button"
          >
            <Plus size={14} className="mr-2" />
            {t('estimates.new_button')}
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={attemptExit}
          className="text-muted-foreground hover:text-foreground uppercase tracking-[0.16em] text-xs"
          data-testid="estimates-back-button"
        >
          <ArrowLeft size={14} className="mr-2" />
          {t('estimates.back_button')}
        </Button>
        {mode === 'builder' ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMode('preview')}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="estimates-preview-button"
          >
            <Eye size={14} className="mr-2" />
            {t('estimates.preview_button')}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMode('builder')}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="estimates-edit-button"
          >
            {t('actions.edit')}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrint}
          className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
          data-testid="estimates-print-button"
        >
          <Printer size={14} className="mr-2" />
          {t('estimates.print_button')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleStartWalkthrough}
          disabled={startingWalkthrough || !current?.id}
          className="group h-10 px-1.5 hover:bg-transparent focus-visible:ring-1 focus-visible:ring-[hsl(var(--primary))]"
          data-testid={
            existingWalkthroughId
              ? 'estimates-pq-review-button'
              : 'estimates-pq-vision-go-button'
          }
          title={
            !current?.id
              ? t('pqv.estimate_unsaved', {
                  defaultValue:
                    'Save the estimate first to start a walkthrough.',
                })
              : undefined
          }
        >
          {/* PQ's own actions wear the PQ| lockup (John, 2026-09-29). */}
          {startingWalkthrough ? (
            <Loader2 size={14} className="mr-2 animate-spin text-[hsl(var(--primary))]" />
          ) : null}
          <PQActionLabel
            monogram="before"
            label={
              existingWalkthroughId
                ? t('pqv.re_view_label', { defaultValue: 'RE-View' })
                : t('pqv.go_label', { defaultValue: 'Vision GO' })
            }
          />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setPqChatOpen(true)}
          disabled={!current?.id}
          className="group h-10 px-1.5 hover:bg-transparent focus-visible:ring-1 focus-visible:ring-[hsl(var(--primary))]"
          data-testid="estimates-ask-pq-button"
          title={
            !current?.id
              ? t('pqchat.estimate_unsaved', {
                  defaultValue: 'Save the estimate first to ask PQ about it.',
                })
              : undefined
          }
        >
          <PQActionLabel
            monogram="after"
            label={t('pqchat.ask_label', { defaultValue: 'Ask' })}
          />
        </Button>
        {current ? (
          <Select
            value={normalizeStatus(current.status || 'draft')}
            onValueChange={handleStatusSelect}
          >
            <SelectTrigger
              className="h-8 w-[150px] border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] uppercase tracking-[0.16em] text-xs bg-transparent"
              data-testid="estimates-status-trigger"
            >
              <SelectValue
                placeholder={t('estimates.list.status', { defaultValue: 'Status' })}
              />
            </SelectTrigger>
            <SelectContent>
              {ESTIMATE_STATUSES.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`estimates-status-option-${s}`}
                >
                  {statusLabel(t, s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConfirmConvert(true)}
          disabled={!current?.id}
          className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
          data-testid="estimates-convert-button"
        >
          <ArrowRightLeft size={14} className="mr-2" />
          {t('estimates.convert_button')}
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
          data-testid="estimates-save-button"
        >
          {saving ? (
            <Loader2 size={14} className="mr-2 animate-spin" />
          ) : (
            <Save size={14} className="mr-2" />
          )}
          {t('actions.save')}
        </Button>
      </div>
    );
  };

  return (
    <div data-testid="estimates-page">
      <PageHeader
        eyebrow={t('estimates.eyebrow')}
        title={
          mode === 'list'
            ? t('estimates.title')
            : current?.project_name || t('estimates.title_new')
        }
        subtitle={
          mode === 'list'
            ? t('estimates.subtitle_list')
            : t('estimates.subtitle_builder')
        }
        actions={renderHeaderActions()}
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="estimates-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {mode === 'list' ? (
        loading ? (
          <EstimatesListSkeleton />
        ) : (
          <div className="space-y-4">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              data-testid="estimates-tabs"
            >
              <TabsList className="bg-[hsl(var(--muted))] border border-border">
                {TAB_IDS.map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className="text-xs uppercase tracking-[0.16em] data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-foreground"
                    data-testid={`estimates-tab-${tab}`}
                  >
                    <span>{statusLabel(t, tab)}</span>
                    <span
                      className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[hsl(var(--card))] px-1.5 text-[10px] font-semibold text-muted-foreground border border-border"
                      data-testid={`estimates-tab-count-${tab}`}
                    >
                      {tabCounts[tab]}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <EstimatesList
              estimates={visibleEstimates}
              onOpen={openEstimate}
              onCreate={startNew}
              onDelete={
                activeTab === 'draft' || activeTab === 'sent'
                  ? (est) => setConfirmDelete(est)
                  : null
              }
            />
          </div>
        )
      ) : null}

      {mode === 'builder' && current ? (
        <div className="space-y-4">
          <EstimateBuilder
            estimate={current}
            clients={clients}
            totals={totals}
            onChange={setCurrent}
            onAddSection={() => setCurrent((c) => addSection(c))}
            onRemoveSection={(id) => setCurrent((c) => removeSection(c, id))}
            onAddBlock={(secId, type) =>
              setCurrent((c) => addBlock(c, secId, type))
            }
            onUpdateBlock={(secId, blockId, patch) =>
              setCurrent((c) => updateBlock(c, secId, blockId, patch))
            }
            onRemoveBlock={(secId, blockId) =>
              setCurrent((c) => removeBlock(c, secId, blockId))
            }
            onMoveBlock={(secId, blockId, dir) =>
              setCurrent((c) => moveBlock(c, secId, blockId, dir))
            }
            onUpdateSection={(id, patch) =>
              setCurrent((c) => updateSection(c, id, patch))
            }
            onAddItem={(secId) => setCurrent((c) => addLineItem(c, secId))}
            onUpdateItem={(secId, itemId, patch) =>
              setCurrent((c) => updateLineItem(c, secId, itemId, patch))
            }
            onRemoveItem={(secId, itemId) =>
              setCurrent((c) => removeLineItem(c, secId, itemId))
            }
            onAddMaterial={(secId) =>
              setCurrent((c) => addMaterialRow(c, secId))
            }
            onUpdateMaterial={(secId, rowId, patch) =>
              setCurrent((c) => updateMaterialRow(c, secId, rowId, patch))
            }
            onRemoveMaterial={(secId, rowId) =>
              setCurrent((c) => removeMaterialRow(c, secId, rowId))
            }
            onAddAllowance={(secId) =>
              setCurrent((c) => addAllowanceRow(c, secId))
            }
            onUpdateAllowance={(secId, rowId, patch) =>
              setCurrent((c) => updateAllowanceRow(c, secId, rowId, patch))
            }
            onRemoveAllowance={(secId, rowId) =>
              setCurrent((c) => removeAllowanceRow(c, secId, rowId))
            }
            onSetBottomLineMode={(secId, on) =>
              setCurrent((c) => setSectionBottomLineMode(c, secId, on))
            }
            onSetBottomLineTotal={(secId, val) =>
              setCurrent((c) => setSectionBottomLineTotal(c, secId, val))
            }
            onSetAllSectionsBottomLineMode={(on) =>
              setCurrent((c) => setAllSectionsBottomLineMode(c, on))
            }
          />
          <PaymentScheduleEditor
            estimate={current}
            totals={totals}
            onAdd={() => setCurrent((c) => addPaymentRow(c))}
            onUpdate={(id, patch) =>
              setCurrent((c) => updatePaymentRow(c, id, patch))
            }
            onRemove={(id) => setCurrent((c) => removePaymentRow(c, id))}
          />
          {/* Pre-con checklist (migration 089) — PQ builds/prepares it in
              chat, so reload whenever the chat closes. */}
          {current.id ? (
            <ChecklistPanel estimateId={current.id} refreshKey={pqChatOpen ? 0 : 1} />
          ) : null}
        </div>
      ) : null}

      {mode === 'preview' && current ? (
        <EstimatePreview
          estimate={current}
          totals={totals}
          company={company}
          edit={{
            // Editable preview: fix words where you read them. Every commit
            // rides the same debounced autosave as PQ Apply, so an edit can
            // never be stranded in unsaved state.
            editable: true,
            onPatchEstimate: (patch) => {
              setCurrent((c) => ({ ...c, ...patch }));
              setPqAutosaveTick((n) => n + 1);
            },
            onPatchOverview: (patch) => {
              setCurrent((c) => ({
                ...c,
                overview: { ...(c?.overview || {}), ...patch },
              }));
              setPqAutosaveTick((n) => n + 1);
            },
            onUpdateSection: (id, patch) => {
              setCurrent((c) => updateSection(c, id, patch));
              setPqAutosaveTick((n) => n + 1);
            },
            onUpdateBlock: (secId, blockId, patch) => {
              setCurrent((c) => updateBlock(c, secId, blockId, patch));
              setPqAutosaveTick((n) => n + 1);
            },
            onUpdateItem: (secId, itemId, patch) => {
              setCurrent((c) => updateLineItem(c, secId, itemId, patch));
              setPqAutosaveTick((n) => n + 1);
            },
          }}
          client={
            current
              ? clients.find(
                  (c) =>
                    c.id === current.client_id ||
                    (c.name &&
                      current.client_name &&
                      String(c.name).trim().toLowerCase() ===
                        String(current.client_name).trim().toLowerCase())
                ) || null
              : null
          }
        />
      ) : null}

      {/* Unsaved-changes guard — shown when leaving the builder with edits. */}
      <AlertDialog open={confirmExit} onOpenChange={(o) => !o && setConfirmExit(false)}>
        <AlertDialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid="estimate-unsaved-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {t('estimates.unsaved_title', {
                defaultValue: 'Discard unsaved changes?',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {t('estimates.unsaved_help', {
                defaultValue:
                  'You have edits that haven’t been saved. Leaving now will lose them.',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
              data-testid="estimate-unsaved-cancel"
            >
              {t('estimates.unsaved_keep_editing', {
                defaultValue: 'Keep editing',
              })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={exitToList}
              className="bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90"
              data-testid="estimate-unsaved-discard"
            >
              {t('estimates.unsaved_discard', {
                defaultValue: 'Discard & leave',
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid="estimate-delete-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              Delete this estimate?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {confirmDelete
                ? `"${confirmDelete.project_name || confirmDelete.title}" will be permanently removed.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-foreground hover:bg-[hsl(var(--muted))]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90"
              data-testid="estimate-delete-confirm-action"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PQChatPanel
        open={pqChatOpen}
        onOpenChange={setPqChatOpen}
        estimateId={current?.id || null}
        isProposalApplied={(p) => (current ? isProposalInEstimate(current, p) : false)}
        estimateTitle={current?.project_name || current?.title || ''}
        onApplyProposal={(proposal) => {
          if (!current) return false;
          // A whole-document draft reports exactly what landed and what it
          // refused to overwrite — worth saying out loud, since "applied"
          // covering 40 changes is not information.
          if (proposal?.kind === 'draft') {
            const result = applyDraftToEstimate(current, proposal);
            setCurrent(result.estimate);
            setPqAutosaveTick((n) => n + 1);
            const summary = result.applied.join(', ');
            toast.success(
              summary
                ? t('pqchat.draft_applied_toast', {
                    defaultValue: 'Added {{summary}} — saving…',
                    summary,
                  })
                : t('pqchat.draft_nothing_new', {
                    defaultValue: 'Nothing new to add — it was already in the estimate.',
                  })
            );
            for (const item of result.skipped) {
              toast.info(
                t('pqchat.draft_skipped_toast', {
                  defaultValue: 'Left alone: {{item}}',
                  item,
                })
              );
            }
            return true;
          }
          setCurrent((c) => applyProposalToEstimate(c, proposal));
          setPqAutosaveTick((n) => n + 1);
          toast.success(
            t('pqchat.applied_toast', {
              defaultValue: 'Added — saving to the estimate…',
            })
          );
          return true;
        }}
      />

      <AlertDialog open={confirmConvert} onOpenChange={setConfirmConvert}>
        <AlertDialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid="estimate-convert-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {t('estimates.convert_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {t('estimates.convert_description', {
                total: totals.total
                  ? `$${totals.total.toLocaleString()}`
                  : '$0',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
              disabled={converting}
            >
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConvert}
              disabled={converting}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="estimate-convert-confirm-action"
            >
              {converting ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : null}
              {t('estimates.create_job')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 27 — outcome-reason capture dialog (Won / Lost). */}
      <Dialog
        open={statusDialog.open}
        onOpenChange={(o) => {
          if (!o) closeStatusDialog();
        }}
      >
        <DialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid="estimate-outcome-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {statusDialog.target === 'lost'
                ? t('estimates.mark_lost_title', {
                    defaultValue: 'Mark estimate as Lost',
                  })
                : t('estimates.mark_won_title', {
                    defaultValue: 'Mark estimate as Won',
                  })}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {statusDialog.target === 'lost'
                ? t('estimates.mark_lost_help', {
                    defaultValue:
                      'Capture why this bid was lost. This is stored for pipeline analytics and can be edited later.',
                  })
                : t('estimates.mark_won_help', {
                    defaultValue:
                      'Optional — capture the reason this bid was won. Helps you spot patterns over time.',
                  })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label
                htmlFor="outcome-reason-select"
                className="text-xs uppercase tracking-[0.16em] text-muted-foreground"
              >
                {t('estimates.outcome_reason_label', {
                  defaultValue: 'Reason',
                })}
                {statusDialog.target && isReasonRequired(statusDialog.target) ? (
                  <span className="ml-1 text-[hsl(var(--destructive))]">*</span>
                ) : null}
              </Label>
              <Select
                value={statusDialog.reason}
                onValueChange={(v) =>
                  setStatusDialog((s) => ({ ...s, reason: v }))
                }
              >
                <SelectTrigger
                  id="outcome-reason-select"
                  className="bg-[hsl(var(--input))] border-border text-foreground"
                  data-testid="estimate-outcome-reason-select"
                >
                  <SelectValue
                    placeholder={t('estimates.outcome_reason_placeholder', {
                      defaultValue: 'Select a reason…',
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {reasonsForStatus(statusDialog.target).map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`estimate-outcome-reason-option-${opt.value}`}
                    >
                      {t(opt.i18nKey, { defaultValue: opt.value })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="outcome-reason-custom"
                className="text-xs uppercase tracking-[0.16em] text-muted-foreground"
              >
                {t('estimates.outcome_reason_notes', {
                  defaultValue: 'Notes (optional override)',
                })}
              </Label>
              <Textarea
                id="outcome-reason-custom"
                value={statusDialog.custom}
                onChange={(e) =>
                  setStatusDialog((s) => ({ ...s, custom: e.target.value }))
                }
                placeholder={t('estimates.outcome_reason_custom_placeholder', {
                  defaultValue:
                    'Add a free-text note. Overrides the dropdown selection when present.',
                })}
                className="bg-[hsl(var(--input))] border-border text-foreground min-h-[80px]"
                data-testid="estimate-outcome-reason-custom"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeStatusDialog}
              disabled={statusDialog.saving}
              className="text-muted-foreground hover:text-foreground"
              data-testid="estimate-outcome-cancel"
            >
              {t('actions.cancel')}
            </Button>
            <Button
              onClick={commitStatusDialog}
              disabled={statusDialog.saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="estimate-outcome-confirm"
            >
              {statusDialog.saving ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : null}
              {statusDialog.target === 'lost'
                ? t('estimates.confirm_lost', { defaultValue: 'Mark as Lost' })
                : t('estimates.confirm_won', { defaultValue: 'Mark as Won' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
